import { type FC, useContext, useEffect, useRef } from 'react';
import { useHorizontalScroll } from '@common/hooks/horizontal-hook';
import {
  type UserFactory,
  type UserFactoryNextToBuild,
} from '@common/types/factoryType';
import { type PlantTypeText } from '@common/types/plant';
import {
  type AvailableQuarry,
  type UserQuarry,
} from '@common/types/quarryType';
import { type ResourceTypeText } from '@common/types/resource';
import mergeRefs from 'merge-refs';

import { ClaimAnimationContext } from '@/context/ClaimAnimationContext';

import { ResourceNavigationItem } from './ResourceNavigationItem';

import css from './styles.module.scss';

interface ResourcesNavigationProps {
  data:
    | {
        list: ResourceTypeText[];
        nextToBuild?: AvailableQuarry;
        buildsData?: Partial<Record<ResourceTypeText, UserQuarry>>;
      }
    | {
        list: PlantTypeText[];
        nextToBuild?: UserFactoryNextToBuild;
        buildsData?: Partial<Record<PlantTypeText, UserFactory>>;
      };
  isAnimationStart?: boolean;
}

const ResourcesNavigation: FC<ResourcesNavigationProps> = ({
  data,
  isAnimationStart,
}) => {
  const { deltaY } = useContext(ClaimAnimationContext);
  const ref = useHorizontalScroll<HTMLDivElement>();
  const refNavigation = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const onScroll = (event: Event): void => {
      if (deltaY && event.target instanceof Element) {
        deltaY.current = event.target.scrollLeft;
      }
    };

    refNavigation.current?.addEventListener('scroll', onScroll);

    return (): void => {
      refNavigation.current?.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    data.buildsData && (
      <nav
        ref={mergeRefs(ref, refNavigation)}
        className={css.resourcesNavigation}
      >
        {data.list?.map((resourceName) => {
          const buildItem =
            data.buildsData &&
            data.buildsData[resourceName as keyof typeof data.buildsData];

          return (
            <ResourceNavigationItem
              key={resourceName}
              resource={resourceName}
              nextToBuild={data.nextToBuild}
              data={buildItem}
              isAnimationStart={isAnimationStart}
            />
          );
        })}
      </nav>
    )
  );
};

export { ResourcesNavigation };
